import { useTranslation } from "react-i18next";
import { Linkedin, Instagram, Youtube, Mail } from "lucide-react";

import PageHero from "@/components/layout/PageHero";
import { VisualSection, VisualMomentsGrid } from "@/components/layout/visualLayout";
import SectionIntro from "@/components/layout/SectionIntro";
import Reveal from "@/components/common/Reveal";
import HoverGlow from "@/components/motion/HoverGlow";
import ASSETS from "@/lib/assets";

export default function MediaKit() {
  const { t } = useTranslation();
  const topics = t("mediaKit.topics", { returnObjects: true });
  const photos = [ASSETS.portrait, ASSETS.editorial[4], ASSETS.editorial[5]];

  return (
    <div data-testid="page-mediakit">
      <PageHero
        eyebrow={t("mediaKit.eyebrow")}
        title={t("mediaKit.title")}
        subtitle={t("mediaKit.lead")}
        image={ASSETS.portrait}
        imageAlt="Fatma Acar — Pressefoto"
        testId="mediakit-hero"
      />

      <VisualSection testId="mediakit-bios">
        <div className="grid md:grid-cols-2 gap-4">
          <Reveal delay={0.05}>
            <HoverGlow className="h-full">
              <div className="wit-card h-full" data-testid="bio-short">
                <p className="wit-eyebrow text-raspberry mb-3">{t("mediaKit.bioShortTitle")}</p>
                <p className="text-ink text-sm leading-relaxed">{t("mediaKit.bioShort")}</p>
              </div>
            </HoverGlow>
          </Reveal>
          <Reveal delay={0.1}>
            <HoverGlow className="h-full">
              <div className="wit-card h-full" data-testid="bio-long">
                <p className="wit-eyebrow text-raspberry mb-3">{t("mediaKit.bioLongTitle")}</p>
                <p className="text-ink text-sm leading-relaxed">{t("mediaKit.bioLong")}</p>
              </div>
            </HoverGlow>
          </Reveal>
        </div>
      </VisualSection>

      <VisualSection testId="mediakit-photos" divider>
        <SectionIntro
          title={t("mediaKit.photosTitle")}
          subtitle={t("mediaKit.photosSubtitle")}
        />
        <VisualMomentsGrid images={photos} testId="press-photos" />
      </VisualSection>

      <VisualSection testId="mediakit-topics">
        <SectionIntro title={t("mediaKit.topicsTitle")} />
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="speaker-topics">
          {Array.isArray(topics) &&
            topics.map((topic, i) => (
              <Reveal key={topic} delay={i * 0.04} as="li">
                <HoverGlow>
                  <div className="wit-card py-5 px-6 flex items-center gap-4" data-testid={`topic-${i}`}>
                    <span className="font-serif text-lg text-raspberry tabular-nums">0{i + 1}</span>
                    <span className="text-ink text-sm">{topic}</span>
                  </div>
                </HoverGlow>
              </Reveal>
            ))}
        </ul>
      </VisualSection>

      <VisualSection testId="mediakit-contact" tight>
        <div className="grid md:grid-cols-2 gap-4">
          <Reveal delay={0.05}>
            <HoverGlow className="h-full">
              <div className="wit-card h-full" data-testid="mediakit-social">
                <p className="wit-eyebrow text-raspberry mb-3">{t("mediaKit.socialTitle")}</p>
                <div className="flex items-center gap-3 mt-2">
                  <a href="https://www.linkedin.com/in/womenintech/" target="_blank" rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full bg-roseMilk flex items-center justify-center text-ink hover:text-raspberry transition-colors cursor-pointer"
                    aria-label="LinkedIn" data-testid="mk-linkedin"><Linkedin size={17} /></a>
                  <a href="https://instagram.com/womenintechhh" target="_blank" rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full bg-lavenderFog flex items-center justify-center text-ink hover:text-raspberry transition-colors cursor-pointer"
                    aria-label="Instagram" data-testid="mk-instagram"><Instagram size={17} /></a>
                  <a href="https://www.youtube.com/@womenintechhh" target="_blank" rel="noopener noreferrer"
                    className="w-11 h-11 rounded-full bg-butterCream flex items-center justify-center text-ink hover:text-raspberry transition-colors cursor-pointer"
                    aria-label="YouTube" data-testid="mk-youtube"><Youtube size={17} /></a>
                </div>
              </div>
            </HoverGlow>
          </Reveal>
          <Reveal delay={0.1}>
            <HoverGlow className="h-full">
              <div className="wit-card h-full" data-testid="mediakit-press">
                <p className="wit-eyebrow text-raspberry mb-3">{t("mediaKit.pressTitle")}</p>
                <a href={`mailto:${t("mediaKit.pressEmail")}`} className="inline-flex items-center gap-2 text-ink hover:text-raspberry text-sm font-medium">
                  <Mail size={16} /> {t("mediaKit.pressEmail")}
                </a>
              </div>
            </HoverGlow>
          </Reveal>
        </div>
      </VisualSection>
    </div>
  );
}
